/**
 * CollectionForm Component
 * Form for creating collections with validation.
 * Currently supports Google Drive collections.
 */

import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { useCreateCollection } from "../../hooks/useCollections";
import type { CollectionCategory } from "../../types/admin";
import { DriveBrowser, type DriveSelection } from "./DriveBrowser";

const collectionFormSchema = z.object({
  slug: z
    .string()
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Gebruik kleine letters, cijfers en koppeltekens")
    .optional()
    .or(z.literal("")),
  name: z.string().min(1, "Naam is verplicht"),
  externalId: z.string().min(1, "External ID is verplicht"),
  driveId: z.string().min(1, "Drive ID is verplicht"),
  folderId: z.string().optional().or(z.literal("")),
  description: z.string().optional().or(z.literal("")),
  defaultPriority: z.string().optional().or(z.literal("")),
  defaultCategory: z
    .enum([
      "statuten",
      "reglement",
      "gedragscode",
      "contract",
      "beleidsplan",
      "besluit",
      "notulen",
      "notitie",
      "jaarverslag",
      "financieel-verslag",
      "voorbeeld",
      "template",
      "onbekend",
    ] as const)
    .optional()
    .or(z.literal("")),
});

type FormData = z.infer<typeof collectionFormSchema>;

interface CollectionFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

// Helper to generate a URL-friendly ID from a name
function generateIdFromName(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 50);
}

export function CollectionForm({ onSuccess, onCancel }: CollectionFormProps) {
  const [isBrowserOpen, setIsBrowserOpen] = useState(false);
  const [driveSelection, setDriveSelection] = useState<DriveSelection | null>(null);
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const createCollection = useCreateCollection();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(collectionFormSchema),
  });

  // Handle selection from Drive browser
  const handleDriveSelect = (selection: DriveSelection) => {
    setSelectionError(null);

    if (selection.type === "file") {
      setSelectionError(
        "Selecteer een gedeelde drive of map. Bestandscollecties worden nog niet ondersteund.",
      );
      return;
    }

    setDriveSelection(selection);
    setValue("name", selection.name);
    setValue("driveId", selection.driveId);
    setValue("externalId", selection.id);
    setValue("folderId", selection.type === "folder" ? selection.id : "");

    const generatedSlug = generateIdFromName(selection.name);
    setValue("slug", generatedSlug);
  };

  const onSubmit = (data: FormData) => {
    const defaultCategory =
      data.defaultCategory === ""
        ? null
        : ((data.defaultCategory as CollectionCategory | undefined) ?? null);

    const requestData = {
      slug: data.slug?.trim() === "" ? undefined : data.slug,
      name: data.name,
      type: "google-drive" as const,
      externalId: data.externalId,
      description: data.description?.trim() === "" ? null : (data.description ?? null),
      config: {
        type: "google-drive" as const,
        driveId: data.driveId,
        ...(data.folderId ? { folderId: data.folderId } : {}),
      },
      defaultPriority: data.defaultPriority ? Number(data.defaultPriority) : null,
      defaultCategory,
    };

    createCollection.mutate(requestData, {
      onSuccess: () => {
        reset();
        setDriveSelection(null);
        setShowAdvanced(false);
        setSelectionError(null);
        onSuccess?.();
      },
    });
  };

  return (
    <div className="bg-surface-container-lowest rounded-lg shadow-3 p-6">
      <h2 className="text-2xl font-bold text-on-surface mb-4">Nieuwe Collectie</h2>

      <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} className="space-y-4">
        {/* Type */}
        <div>
          <label className="block text-sm font-semibold text-on-surface-variant mb-2">Type</label>
          <div className="rounded-md border border-outline-variant px-3 py-2 text-sm text-on-surface">
            📁 Google Drive
          </div>
        </div>

        {/* Google Drive Browser Button */}
        <div>
          <button
            type="button"
            onClick={() => setIsBrowserOpen(true)}
            className="w-full py-3 px-4 border-2 border-dashed border-primary/40 rounded-md text-primary hover:bg-primary-container/30 hover:border-primary/60 transition-colors font-medium"
          >
            📁 Bladeren in Google Drive...
          </button>

          {selectionError && <p className="mt-2 text-sm text-error">{selectionError}</p>}

          {driveSelection && (
            <div className="mt-2 p-3 bg-primary-container/30 rounded-md border border-primary/20">
              <div className="flex items-center gap-2">
                <span className="text-lg">{driveSelection.type === "folder" ? "📁" : "🗄️"}</span>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-on-surface truncate">{driveSelection.name}</p>
                  {driveSelection.path.length > 0 && (
                    <p className="text-xs text-on-surface-variant truncate">
                      {driveSelection.path.join(" / ")}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setDriveSelection(null)}
                  className="text-primary hover:text-primary/80 text-sm"
                >
                  Wijzig
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Slug Field */}
        <div>
          <label className="block text-sm font-semibold text-on-surface-variant mb-1">
            Slug (optioneel)
          </label>
          <input
            {...register("slug")}
            type="text"
            placeholder="bijv: groepsraad-documenten"
            className="w-full px-3 py-2 border border-outline-variant rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
          />
          {errors.slug && (
            <p className="mt-1 text-sm text-error">{errors.slug.message as string}</p>
          )}
        </div>

        {/* Name Field */}
        <div>
          <label className="block text-sm font-semibold text-on-surface-variant mb-1">Naam</label>
          <input
            {...register("name")}
            type="text"
            placeholder="Naam van de collectie"
            className="w-full px-3 py-2 border border-outline-variant rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
          />
          {errors.name && (
            <p className="mt-1 text-sm text-error">{errors.name.message as string}</p>
          )}
        </div>

        {/* Manual Google Drive fields */}
        <div>
          <label className="block text-sm font-semibold text-on-surface-variant mb-1">
            Drive ID
          </label>
          <input
            {...register("driveId")}
            type="text"
            placeholder="Google Shared Drive ID"
            className="w-full px-3 py-2 border border-outline-variant rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
          />
          {errors.driveId && (
            <p className="mt-1 text-sm text-error">{errors.driveId.message as string}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-semibold text-on-surface-variant mb-1">
            Folder ID (optioneel)
          </label>
          <input
            {...register("folderId")}
            type="text"
            placeholder="Alleen documenten binnen deze map"
            className="w-full px-3 py-2 border border-outline-variant rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-on-surface-variant mb-1">
            External ID
          </label>
          <input
            {...register("externalId")}
            type="text"
            placeholder="Unieke bron-ID (bijv. drive of map ID)"
            className="w-full px-3 py-2 border border-outline-variant rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
          />
          {errors.externalId && (
            <p className="mt-1 text-sm text-error">{errors.externalId.message as string}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-semibold text-on-surface-variant mb-1">
            Omschrijving (optioneel)
          </label>
          <input
            {...register("description")}
            type="text"
            placeholder="Korte omschrijving van deze collectie"
            className="w-full px-3 py-2 border border-outline-variant rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>

        {/* Advanced Settings (Default Metadata) */}
        <div className="border border-outline-variant rounded-md overflow-hidden">
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="w-full px-4 py-3 bg-surface-container-low hover:bg-surface-container flex items-center justify-between text-left transition-colors"
          >
            <span className="text-sm font-semibold text-on-surface-variant">
              ⚙️ Geavanceerde instellingen
            </span>
            <span className="text-on-surface-variant">{showAdvanced ? "▲" : "▼"}</span>
          </button>

          {showAdvanced && (
            <div className="p-4 border-t border-outline-variant space-y-4 bg-surface-container-lowest">
              <p className="text-xs text-on-surface-variant">
                Standaardwaarden voor documenten in deze collectie.
              </p>

              {/* Default Category */}
              <div>
                <label className="block text-sm font-semibold text-on-surface-variant mb-1">
                  Standaard categorie
                </label>
                <select
                  {...register("defaultCategory")}
                  className="w-full px-3 py-2 border border-outline-variant rounded-md focus:ring-2 focus:ring-primary focus:border-transparent bg-surface"
                >
                  <option value="">-- Geen standaard --</option>
                  <option value="statuten">Statuten</option>
                  <option value="reglement">Huishoudelijk Reglement</option>
                  <option value="gedragscode">Gedragscode</option>
                  <option value="contract">Contract</option>
                  <option value="beleidsplan">Beleidsplan</option>
                  <option value="besluit">Besluit</option>
                  <option value="notulen">Notulen</option>
                  <option value="notitie">Notitie</option>
                  <option value="jaarverslag">Jaarverslag</option>
                  <option value="financieel-verslag">Financieel verslag</option>
                  <option value="voorbeeld">Voorbeeld</option>
                  <option value="template">Template</option>
                  <option value="onbekend">Onbekend</option>
                </select>
              </div>

              {/* Default Priority */}
              <div>
                <label className="block text-sm font-semibold text-on-surface-variant mb-1">
                  Standaard prioriteit (1-1000)
                </label>
                <input
                  {...register("defaultPriority")}
                  type="number"
                  min={1}
                  max={1000}
                  placeholder="bijv. 500"
                  className="w-full px-3 py-2 border border-outline-variant rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>
          )}
        </div>

        {/* Error Message */}
        {createCollection.isError && (
          <div className="bg-error-container border border-error/30 text-on-error-container px-4 py-3 rounded-md">
            <p className="text-sm">{(createCollection.error as Error).message}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-2 pt-2">
          <button
            type="submit"
            disabled={createCollection.isPending}
            className="flex-1 bg-primary hover:bg-primary/90 disabled:bg-on-surface/12 text-on-primary px-4 py-2 rounded-md font-semibold transition-colors"
          >
            {createCollection.isPending ? "Aanmaken..." : "Collectie aanmaken & ontdekken"}
          </button>
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 bg-surface-container hover:bg-surface-container-high text-on-surface rounded-md font-semibold transition-colors"
            >
              Annuleer
            </button>
          )}
        </div>
      </form>

      {/* Drive Browser Modal */}
      <DriveBrowser
        isOpen={isBrowserOpen}
        onClose={() => setIsBrowserOpen(false)}
        onSelect={handleDriveSelect}
      />
    </div>
  );
}
